import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CompanyDetail } from "@app/shared";
import { adminApi } from "./api";
import { companiesApi } from "../companies/api";
import { Button, Card, PageTitle } from "../../components/ui";
import { QueryView } from "../../components/states";
import { LocationPicker } from "../../components/map/CompanyMap";

// 承認前に管理者が座標を補完・修正するピッカー（F-16・§2.6）。
// 自己登録でピンを置かなかった企業の座標を、審査時に管理者が入れられるようにする
// （F-18/F-19 が空にならないための充足経路）。フル項目を PUT で送るため既存値を引き継ぐ。
function ReviewLocationEditor({ company }: { company: CompanyDetail }) {
  const qc = useQueryClient();
  const [coords, setCoords] = useState<{ latitude: number | null; longitude: number | null }>({
    latitude: company.latitude,
    longitude: company.longitude,
  });

  const save = useMutation({
    mutationFn: (c: { latitude: number | null; longitude: number | null }) =>
      companiesApi.updateProfile(company.id, {
        name: company.name,
        area: company.area,
        industryCode: company.industryCode,
        jobTypes: company.jobTypes,
        description: company.description,
        capacity: company.capacity,
        conditions: company.conditions,
        contactName: company.contactName,
        contactEmail: company.contactEmail,
        contactPhone: company.contactPhone ?? undefined,
        latitude: c.latitude,
        longitude: c.longitude,
        isPublic: company.isPublic,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });

  return (
    <div className="space-y-2 rounded-soft bg-sky-soft p-3">
      <LocationPicker
        value={coords}
        onChange={(v) => setCoords(v ?? { latitude: null, longitude: null })}
      />
      <div className="flex items-center justify-end gap-2">
        {save.isSuccess && <span className="text-xs font-bold text-emerald-600">保存しました</span>}
        <Button onClick={() => save.mutate(coords)} disabled={save.isPending}>
          所在地を保存
        </Button>
      </div>
    </div>
  );
}

export function AdminReviewsPage() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["admin", "companies", "pending"],
    queryFn: () => adminApi.listCompanies("pending"),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin"] });
  };
  const approve = useMutation({ mutationFn: adminApi.approveCompany, onSuccess: invalidate });
  const reject = useMutation({
    mutationFn: (id: string) => adminApi.rejectCompany(id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-6">
      <PageTitle title="企業審査" subtitle="自己登録された企業を審査します。" />
      <QueryView
        query={query}
        emptyWhen={(d) => d.items.length === 0}
        emptyMessage="審査待ちの企業はありません。"
      >
        {(data) => (
          <div className="space-y-3">
            {data.items.map((c) => (
              <Card key={c.id} className="space-y-2">
                <h3 className="text-base font-extrabold text-brand-dark">{c.name}</h3>
                <p className="text-xs text-slate-400">
                  {c.area}・{c.industryLabel}・受入可能 {c.capacity}人
                </p>
                <p className="text-sm text-slate-600">{c.description}</p>
                <p className="text-xs text-slate-400">
                  担当: {c.contactName} / {c.contactEmail}
                  {c.contactPhone ? ` / ${c.contactPhone}` : ""}
                </p>
                <p className="text-xs text-slate-400">
                  所在地の地図: {c.latitude != null ? "設定あり" : "未設定"}
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button onClick={() => approve.mutate(c.id)} disabled={approve.isPending}>
                    承認して公開
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (confirm("この企業を却下しますか？")) reject.mutate(c.id);
                    }}
                    disabled={reject.isPending}
                  >
                    却下
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setOpenId((id) => (id === c.id ? null : c.id))}
                  >
                    {openId === c.id ? "所在地を閉じる" : "所在地を設定"}
                  </Button>
                </div>
                {openId === c.id && <ReviewLocationEditor company={c} />}
              </Card>
            ))}
          </div>
        )}
      </QueryView>
    </div>
  );
}
